<template>
  <div class="w-full" :class="themeColor.background">
    <div
      class="flex flex-col lg:flex-row h-full max-w-7xl items-center justify-center mx-auto"
      :class="itemPlacement"
    >
      <div
        class="hero-content flex h-full flex-col justify-center self-center px-4 py-8 sm:p-12 lg:w-1/2 lg:text-left lg:justify-start"
        :class="[ addMarginTop ? 'mt-20' : '', typography.textBox ]"
      >
        <TypeOverline
          tag="p"
          variant="large"
          class="text-secondary my-4 text-[13px] tracking-[3px]"
        >
          <span v-html="title" class="richText" />
        </TypeOverline>
        <TypeDisplay
          tag="h1"
          class="mb-4 font-poppins"
          :class="[typography.headline, themeColor.headline]"
        >
          <span v-html="headline" class="richText" />
        </TypeDisplay>
        <TypeBody
          variant="medium"
          class="leading-6 mb-4"
          :class="[typography.body, themeColor.body]"
        >
          <span v-html="body" class="richText" />
        </TypeBody>
        <form @submit.prevent="handleSubmit" v-if="addRegistrationPrompt">
          <ReasonDropdown
          :dropdown-label="``"
          placeholder="What brought you to Bambee?"
          class="w-full text-center dropdown lg:w-4/5"
          data-qa="bambee-interest-dropdown-select"
          input-size="large"
          />
          <BaseButton
            role="button"
            variant="secondary"
            post-icon="arrowRight"
            size="giant"
            block
            class="self-center my-2 text-body leading-6 font-semibold lg:my-4 lg:w-4/5 lg:self-start lg:h-14"
            @click="$router.push(link)"
          >
            <TypeBody
              tag="span"
              variant="text"
              weight="regular"
              class="text-body font-semibold leading-6 mr-3"
            >
              {{ buttonText }}
            </TypeBody>
          </BaseButton>
          <img :src="subImage" :alt="subImageExplainer"/>
        </form>
        <BaseButton
          v-if="!addRegistrationPrompt"
          tag="button"
          role="button"
          variant="secondary"
          post-icon="arrowRight"
          class="self-center my-8 flex flex-row px-2 text-body leading-6 font-semibold w-fit h-12 lg:self-start lg:h-14"
          @click="$router.push(link)"
        >
          <TypeBody
            tag="span"
            variant="text"
            weight="regular"
            class="text-body font-semibold leading-6 mr-3"
          >
            {{ buttonText }}
          </TypeBody>
        </BaseButton>
      </div>
      <div
        class="flex flex-row justify-center w-full md:w-3/4 lg:w-1/2"
        :class="[addMarginTop ? 'mt-20' : '', imagePadding, imagebottomMargin]"
      >
        <img
          :src="image"
          :alt="imageExplainerText"
          :loading="loadingAttribute"
          :class="[objFit, objectPosition]"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from '@nuxtjs/composition-api';
import {
  BaseIcon,
  TypeBody,
  TypeDisplay,
  TypeOverline,
  BaseButton,
} from '@bambeehr/pollen';
import HeroLarge from '@/assets/images/hero/hero-lg.png';
import ReasonDropdown from '@/components/ReasonDropdown.vue';

export default defineComponent({
  name: 'Hero',
  components: {
    BaseIcon,
    TypeBody,
    TypeDisplay,
    TypeOverline,
    BaseButton,
    ReasonDropdown,
},
  props: {
    title: {
      type: String,
      default: 'The Only Dedicated HR Manager Solution',
    },
    headline: {
      type: String,
      default:
        'Performance Management and Feedback for Happy and Productive Workers',
    },
    body: {
      type: String,
      default:
        'Structured feedback and routine performance management can drive productivity and improve the employee experience. Bambee’s performance management tools and expert HR guidance will help you cultivate a highly productive workforce.',
    },
    buttonText: {
      type: String,
      default: 'See how we can help',
    },
    image: {
      type: String,
      default: HeroLarge,
    },
    lazyLoad: {
      type: Boolean,
      default: false,
    },
    imageExplainerText: {
      type: String,
      default: '',
      required: true,
    },
    cropLocation: {
      type: String,
      default: 'center',
    },
    link: {
      type: String,
      required: true,
    },
    colorTheme: {
      type: String,
      default: 'light',
    },
    isImageFullBleed: {
      type: Boolean,
      default: false,
    },
    addMarginTop: {
      type: Boolean,
      default: false,
    },
    addResponsiveBottomMargin: {
      type: Boolean,
      default: true,
    },
    addRegistrationPrompt: {
      type: Boolean,
      default: false,
    },
    subImage: {
      type:String,
    },
    subImageExplainer: {
      type:String,
    },
    typographyVariant: {
      type:String,
      default:'default',
    },
  },
  setup(props) {
    const loadingAttribute = computed(() =>
      props.lazyLoad ? 'lazy' : 'eager'
    );
    const objectPosition = computed(() => {
      switch (props.cropLocation) {
        case 'left':
          return 'object-left';
        case 'right':
          return 'object-right';
        case 'bottom':
          return 'object-bottom';
        default:
          return 'object-center';
      }
    });

    const themeColor = computed(() => {
      const theme = {};
      if (props.colorTheme === 'light') {
        theme.background = 'bg-transparent';
        theme.headline = 'text-grayHeader';
        theme.body = 'text-grayBody';
      } else {
        theme.background = 'bg-brandPrimary';
        theme.headline = 'text-white';
        theme.body = 'text-grayInput';
      }

      return theme;
    });

    const typography = computed(() => {
      const fontSet = {};
      switch(props.typographyVariant) {
        case 'dollarTrial':
          fontSet.title = '';
          fontSet.headline = 'text-[44px] leading-[56px] lg:text-[72px] lg:leading-[88px] purpleGradient';
          fontSet.body = 'text-[18px]';
          break;
        default:
          fontSet.textBox = 'text-center lg:px-12 xl:px-16 2xl:px-20';
          fontSet.headline = 'text-headTitle text-[32px] xl:text-[40px] tracking-[0.1px]';
          fontSet.body = 'text-body text-[15px]';
      }

      return fontSet;
    })

    const itemPlacement = computed(() => {
      return props.isImageFullBleed ? 'lg:items-stretch' : '';
    });

    const imagePadding = computed(() => {
      return props.isImageFullBleed ? '' : 'px-4 lg:px-6 lg:py-10';
    });

    const objFit = computed(() => {
      return props.isImageFullBleed ? 'object-cover' : 'object-scale-down';
    });

    const imagebottomMargin = computed(() => {
      return !props.isImageFullBleed && props.addResponsiveBottomMargin
        ? 'mb-16 sm:mb-20 lg:mb-0'
        : '';
    });

    return {
      loadingAttribute,
      objectPosition,
      themeColor,
      itemPlacement,
      imagePadding,
      objFit,
      imagebottomMargin,
      typography,
    };
  },
});
</script>
<style scoped>
@media (min-width: 390px) {
  .hero-content {
    @apply flex-row;
  }
}

.dropdown :nth-child(1) {
  background-color:#FCFCFC;
}

.purpleGradient {
  background: #30074D;
  background: linear-gradient(to right, #30074D 0%, #57088F 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
